import React from 'react';
import { Button, Select, Text, TextField, type SelectOption } from '@chatsdet/ui';
import type { ApiSequenceFormPart } from '@chatsdet/types';
import { Trash, Plus } from '@phosphor-icons/react';
import { MacroRichTextField } from './MacroRichTextField';
import { AttachmentPickerDialog } from '../../../features/attachments/components/AttachmentPickerDialog';
import { useResolvedAttachments } from '../../../features/attachments/api';

const KIND_OPTIONS: SelectOption[] = [
  { id: 'text', label: 'Text' },
  { id: 'file', label: 'File' },
];

function makeDefaultPart(kind: 'text' | 'file', name: string): ApiSequenceFormPart {
  return kind === 'text' ? { kind: 'text', name, value: '' } : { kind: 'file', name, attachmentId: '' };
}

type FormPartsEditorProps = {
  parts: ApiSequenceFormPart[];
  onChange: (parts: ApiSequenceFormPart[]) => void;
};

/**
 * Repeatable list of multipart/form-data parts for a step. Mirrors the
 * queryParams/headersTemplate row patterns in SortableStepCard, but each row
 * additionally toggles between a text value and a file (sourced from an
 * attachment) since ApiSequenceFormPart is a discriminated union.
 */
export function FormPartsEditor({ parts, onChange }: FormPartsEditorProps) {
  const attachmentIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (const part of parts) {
      if (part.kind === 'file' && part.attachmentId) ids.add(part.attachmentId);
    }
    return Array.from(ids);
  }, [parts]);
  const resolved = useResolvedAttachments(attachmentIds);
  const attachmentById = React.useMemo(() => {
    const map = new Map<string, { id: string; filename: string }>();
    for (const attachment of resolved.data?.data ?? []) {
      map.set(attachment.id, attachment);
    }
    return map;
  }, [resolved.data]);

  const updatePart = (idx: number, next: ApiSequenceFormPart) => {
    const nextParts = [...parts];
    nextParts[idx] = next;
    onChange(nextParts);
  };

  const removePart = (idx: number) => {
    onChange(parts.filter((_, i) => i !== idx));
  };

  return (
    <div className="space-y-2">
      {parts.map((part, idx) => (
        <div key={idx} className="rounded-md border border-border p-3 space-y-2">
          <div className="flex items-center gap-2">
            <TextField
              aria-label={`Form part ${idx + 1} name`}
              value={part.name}
              onChange={(val) => updatePart(idx, { ...part, name: val })}
              placeholder="field name"
              className="font-mono flex-1"
            />
            <Select
              aria-label={`Form part ${idx + 1} kind`}
              items={KIND_OPTIONS}
              selectedKey={part.kind}
              onSelectionChange={(key) => {
                if (key === part.kind) return;
                updatePart(idx, makeDefaultPart(key === 'file' ? 'file' : 'text', part.name));
              }}
              className="w-28"
            />
            <Button
              variant="icon"
              iconOnly
              size="sm"
              onPress={() => removePart(idx)}
              tooltip="Remove"
            >
              <Trash size={14} />
            </Button>
          </div>

          {part.kind === 'text' ? (
            <MacroRichTextField
              aria-label={`Form part ${idx + 1} value`}
              value={part.value}
              onChange={(val) => updatePart(idx, { ...part, value: val })}
              placeholder="value"
              description="Supports $MACRO(), {{stepId.response.path}}, and $var.name syntax, same as the body template."
              className="font-mono text-sm"
            />
          ) : (
            <div className="space-y-2">
              {part.attachmentId && attachmentById.has(part.attachmentId) ? (
                <div className="flex items-center gap-2 rounded-md bg-muted/30 px-3 py-2">
                  <Text size="sm" className="flex-1 truncate">
                    {attachmentById.get(part.attachmentId)?.filename}
                  </Text>
                  <AttachmentPickerDialog
                    selectedIds={[part.attachmentId]}
                    onChange={(nextIds) =>
                      updatePart(idx, { ...part, attachmentId: nextIds[0] ?? '' })
                    }
                    triggerVariant="ghost"
                    triggerSize="sm"
                    triggerLabel="Replace"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    onPress={() => updatePart(idx, { ...part, attachmentId: '' })}
                  >
                    Remove
                  </Button>
                </div>
              ) : (
                <AttachmentPickerDialog
                  selectedIds={part.attachmentId ? [part.attachmentId] : []}
                  onChange={(nextIds) =>
                    updatePart(idx, { ...part, attachmentId: nextIds[0] ?? '' })
                  }
                  triggerVariant="outline"
                  triggerSize="sm"
                  triggerLabel="Choose attachment"
                />
              )}
              <TextField
                aria-label={`Form part ${idx + 1} filename override`}
                value={part.filename ?? ''}
                onChange={(val) => updatePart(idx, { ...part, filename: val || undefined })}
                placeholder="Filename (defaults to the attachment's own filename)"
              />
            </div>
          )}
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        onPress={() => onChange([...parts, makeDefaultPart('text', '')])}
        iconLeft={<Plus size={14} />}
      >
        Add
      </Button>
    </div>
  );
}
